import React, { useState, useMemo, useEffect } from 'react';
import {
  collection,
  getDocs,
  addDoc,
  deleteDoc,
  query,
  where,
  doc,
  setDoc,
  writeBatch,
  serverTimestamp,
  getDoc,
} from 'firebase/firestore';
import { db } from '../../../firebase/config';

const MS_PER_DAY = 24 * 60 * 60 * 1000;

const pad2 = (n) => String(n).padStart(2, "0");

const fmtYMD = (d) =>
  `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

// Parse "YYYY-MM-DD" en LOCAL (y al medio día para evitar DST)
const parseYMDLocal = (ymd) => {
  const [y, m, d] = (ymd || "").split("-").map(Number);
  return new Date(y, (m || 1) - 1, d || 1, 12, 0, 0);
};

const addMin = (d, m) => new Date(d.getTime() + m * 60000);
const fmt = (n) => String(n).padStart(2, '0');
const toHHmm = (d) => `${fmt(d.getHours())}:${fmt(d.getMinutes())}`;

/**
 * Slots reales cada 30 minutos entre start y end (incluye end)
 */
const buildSlots30 = (ymd, start = "07:30", end = "20:00") => {
  const slots = [];
  let cur = new Date(`${ymd}T${start}`);
  const endD = new Date(`${ymd}T${end}`);

  while (cur <= endD) {
    slots.push(toHHmm(cur));
    cur = addMin(cur, 30);
  }
  return slots;
};

// Solo lunes a viernes
const weekDays = [
  { k: '1', label: 'Lunes' },
  { k: '2', label: 'Martes' },
  { k: '3', label: 'Miércoles' },
  { k: '4', label: 'Jueves' },
  { k: '5', label: 'Viernes' },
  { k: '6', label: 'Sábado' },
  { k: '7', label: 'Domingo' },
];

/**
 * Modal para editar breaks y horario base.
 *
 * Props:
 *  - open
 *  - onClose
 *  - selectedDate (opcional, para centrarte en el día actual)
 *  - weeklyTemplate
 *  - setWeeklyTemplate
 *  - profesionalId (opcional, para guardar por profesional)
 */
export default function BreakEditorModal({
  open,
  onClose,
  selectedDate,
  weeklyTemplate,
  setWeeklyTemplate,
  profesionalId = null,
}) {
  const todayStr = fmtYMD(new Date());

  // mañana por defecto
  const tomorrowStr = useMemo(() => {
    const d = new Date();
    d.setTime(d.getTime() + MS_PER_DAY);
    return fmtYMD(d);
  }, []);

  const [tab, setTab] = useState('daily'); // 'weekly' | 'daily' | 'base'
  const [selectedDays, setSelectedDays] = useState([tomorrowStr]);
  const [dayBreaks, setDayBreaks] = useState({}); // { 'YYYY-MM-DD': ['07:30','08:40', ...] }
  const [saving, setSaving] = useState(false);
  const [newDate, setNewDate] = useState(tomorrowStr);

  // Horario semanal del profesional
  const [weeklySchedule, setWeeklySchedule] = useState({});
  const [loadingBase, setLoadingBase] = useState(false);

  const slots = useMemo(
    () => buildSlots30(fmtYMD(new Date()), '07:30', '20:00'),
    []
  );

  // --- Cargar breaks desde Firestore (por fechas) ---
  const fetchBreaksForDate = async (date) => {
    try {
      let qRef = query(collection(db, "breaks"), where("fecha", "==", date));
      if (profesionalId) {
        qRef = query(qRef, where("profesionalId", "==", profesionalId));
      }

      const snap = await getDocs(qRef);

      const starts = snap.docs
        .map((d) => d.data()?.start)
        .filter(Boolean);

      starts.sort();
      return starts;
    } catch (e) {
      console.error("Error cargando breaks del día:", date, e);
      return [];
    }
  };

  const hydrateDaysFromDB = async (dates) => {
    const unique = Array.from(new Set((dates || []).filter(Boolean)));

    const pairs = await Promise.all(
      unique.map(async (date) => [date, await fetchBreaksForDate(date)])
    );

    setSelectedDays(unique);
    setDayBreaks((prev) => {
      const next = { ...prev };
      for (const [date, starts] of pairs) next[date] = starts;
      return next;
    });
  };

  // --- Traer fechas (YYYY-MM-DD) que tienen breaks guardados ---
  const fetchDatesWithBreaks = async () => {
    try {
      let qRef = query(
        collection(db, "breaks"),
        where("fecha", ">=", todayStr) // solo hoy/futuro (como tus validaciones)
      );

      if (profesionalId) {
        qRef = query(qRef, where("profesionalId", "==", profesionalId));
      } else {
        // si no hay profesional, guardas profesionalId:null
        qRef = query(qRef, where("profesionalId", "==", null));
      }

      const snap = await getDocs(qRef);

      // dedupe de fechas
      const set = new Set();
      snap.docs.forEach((d) => {
        const f = d.data()?.fecha;
        if (f) set.add(f);
      });

      // orden asc (string YYYY-MM-DD ordena bien)
      return Array.from(set).sort();
    } catch (e) {
      console.error("Error cargando fechas con breaks:", e);
      return [];
    }
  };


  // Cargar horario semanal + plantilla semanal desde breaks_weekly/{profId}
  useEffect(() => {
    if (!open) return;

    const docId = profesionalId || 'default';
    const ref = doc(db, 'breaks_weekly', docId);

    const load = async () => {
      try {
        setLoadingBase(true);

        const snap = await getDoc(ref);

        if (snap.exists()) {
          const data = snap.data();

          // plantilla semanal existente
          if (data?.defaultWeekly && setWeeklyTemplate) {
            setWeeklyTemplate(data.defaultWeekly);
          }

          // NUEVO SISTEMA
          if (data?.weeklySchedule) {
            setWeeklySchedule(data.weeklySchedule);
          }

          // FALLBACK DESDE horarioBase
          else if (data?.horarioBase) {
            const start = data.horarioBase.start || '07:30';
            const end = data.horarioBase.end || '20:00';

            const fallbackSchedule = {};

            weekDays.forEach((d) => {
              fallbackSchedule[d.k] = {
                enabled: d.k !== '6' && d.k !== '7',
                start,
                end,
              };
            });

            setWeeklySchedule(fallbackSchedule);
          }

          // FALLBACK TOTAL
          else {
            const fallbackSchedule = {};

            weekDays.forEach((d) => {
              fallbackSchedule[d.k] = {
                enabled: d.k !== '6' && d.k !== '7',
                start: '07:30',
                end: '20:00',
              };
            });

            setWeeklySchedule(fallbackSchedule);
          }
        }
      } catch (err) {
        console.error('Error cargando horario semanal:', err);
      } finally {
        setLoadingBase(false);
      }
    };

    load();
  }, [open, profesionalId, setWeeklyTemplate]);

  useEffect(() => {
    if (!open) return;

    const run = async () => {
      const initial =
        typeof selectedDate === "string" && selectedDate
          ? selectedDate
          : tomorrowStr;

      // 1) traer todos los días con breaks
      const datesFromDB = await fetchDatesWithBreaks();

      // 2) asegurar que el "initial" exista en la lista (aunque no tenga breaks aún)
      const finalDates = Array.from(new Set([initial, ...datesFromDB])).sort();

      // 3) hidratar breaks por cada fecha
      await hydrateDaysFromDB(finalDates);

      // 4) setear el date picker al initial
      setNewDate(initial);
    };

    run();
  }, [open, profesionalId, selectedDate, tomorrowStr]);


  if (!open) return null;

  const isPastOrToday = (ymd) => ymd <= todayStr;

  const toggleBreak = (date, hhmm) => {
    setDayBreaks((prev) => {
      const prevForDate = prev[date] || [];
      return {
        ...prev,
        [date]: prevForDate.includes(hhmm)
          ? prevForDate.filter((x) => x !== hhmm)
          : [...prevForDate, hhmm],
      };
    });
  };

  const toggleAllDay = (date) => {
    const allSelected = (dayBreaks[date]?.length || 0) === slots.length;
    setDayBreaks((prev) => ({
      ...prev,
      [date]: allSelected ? [] : [...slots],
    }));
  };

  const removeDay = (date) => {
    setSelectedDays((prev) => prev.filter((d) => d !== date));
    setDayBreaks((prev) => {
      const cp = { ...prev };
      delete cp[date];
      return cp;
    });
  };

  const addDay = async () => {
    if (!newDate) return;

    setSelectedDays((prev) =>
      prev.includes(newDate) ? prev : [...prev, newDate]
    );

    const starts = await fetchBreaksForDate(newDate);

    setDayBreaks((prev) => ({
      ...prev,
      [newDate]: starts || [],
    }));
  };

  // ---------- Guardar días específicos con validaciones ----------
  const saveDaily = async () => {
    try {
      if (!selectedDays.length) {
        alert('No hay días seleccionados.');
        return;
      }

      setSaving(true);

      // 2) Guardar breaks (sin tocar reservas)
      for (const date of Object.keys(dayBreaks)) {
        let qOld = query(
          collection(db, 'breaks'),
          where('fecha', '==', date)
        );
        if (profesionalId) {
          qOld = query(qOld, where('profesionalId', '==', profesionalId));
        }

        const old = await getDocs(qOld);
        const batch = writeBatch(db);

        // borra los breaks previos del día para ese profesional
        old.forEach((d) => batch.delete(d.ref));

        // crea breaks seleccionados
        (dayBreaks[date] || []).forEach((h) => {
          const ref = doc(collection(db, 'breaks'));
          batch.set(ref, {
            fecha: date,
            start: h,
            end: toHHmm(addMin(new Date(`${date}T${h}`), 30)), // ✅ bloque 30m
            flexible: false,
            profesionalId: profesionalId || null,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          });
        });

        await batch.commit();
      }

      alert('Breaks guardados correctamente.');
      onClose?.();
    } catch (err) {
      console.error(err);
      alert('Error al guardar los breaks.');
    } finally {
      setSaving(false);
    }
  };

  // ---------- Guardar plantilla semanal ----------
  const saveWeekly = async () => {
    const docId = profesionalId || 'default';
    await setDoc(
      doc(db, 'breaks_weekly', docId),
      {
        defaultWeekly: weeklyTemplate || {},
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
    alert('Plantilla semanal guardada.');
    onClose?.();
  };

    // ---------- Guardar horario base ----------
  const saveHorarioBase = async () => {
    try {
      for (const dayKey of Object.keys(weeklySchedule)) {
        const day = weeklySchedule[dayKey];

        if (
          day?.enabled &&
          day?.start &&
          day?.end &&
          day.start >= day.end
        ) {
          const dayLabel =
            weekDays.find((x) => x.k === dayKey)?.label || dayKey;

          alert(`El horario de ${dayLabel} es inválido.`);
          return;
        }
      }

      const docId = profesionalId || 'default';

      await setDoc(
        doc(db, 'breaks_weekly', docId),
        {
          weeklySchedule,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      alert('Horario semanal guardado correctamente.');
      onClose?.();

    } catch (err) {
      console.error('Error guardando horario semanal:', err);
      alert('No se pudo guardar el horario semanal.');
    }
  };

  // ---------- Subcomponentes UI ----------
  const DayCard = ({ date }) => {
  const label = parseYMDLocal(date).toLocaleDateString("es-CL", {
    weekday: "long",
    day: "numeric",
    month: "long",
  });
    const todos = (dayBreaks[date]?.length || 0) === slots.length;

    return (
      <div className="border rounded-xl p-4 bg-white">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-medium capitalize">{label}</h4>
          <div className="flex items-center gap-2">
            <button
              onClick={() => toggleAllDay(date)}
              className="text-xs px-2 py-1 border rounded-md hover:bg-slate-50"
            >
              {todos ? 'Desmarcar todo' : 'Todo el día como break'}
            </button>
            <button
              onClick={() => removeDay(date)}
              className="text-xs px-2 py-1 border border-rose-300 text-rose-700 rounded-md hover:bg-rose-50"
              title="Eliminar este día"
            >
              Eliminar día
            </button>
          </div>
        </div>

        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
          {slots.map((h) => {
            const isBreak = dayBreaks[date]?.includes(h);
            return (
              <button
                key={h}
                onClick={() => toggleBreak(date, h)}
                className={`text-xs px-2 py-1 rounded-md border ${
                  isBreak
                    ? 'bg-amber-100 border-amber-400 text-amber-800'
                    : 'border-slate-200 hover:bg-slate-50'
                }`}
              >
                {h}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const WeeklyEditor = () => (
    <div className="space-y-4">
      {weekDays.map((d) => {
        if (!weeklySchedule?.[d.k]?.enabled) return null;
        const rows = weeklyTemplate?.[d.k] || [];
        const setRows = (setter) =>
          setWeeklyTemplate((prev) => ({
            ...(prev || {}),
            [d.k]:
              typeof setter === 'function'
                ? setter(prev?.[d.k] || [])
                : setter,
          }));

        return (
          <div key={d.k} className="border rounded-xl p-3 bg-white">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-medium">{d.label}</h4>
              <button
                onClick={() =>
                  setRows((prev) => [
                    ...(prev || []),
                    { start: '13:00', end: '14:00' },
                  ])
                }
                className="text-xs px-2 py-1 border rounded-md hover:bg-slate-50"
              >
                + Agregar bloque
              </button>
            </div>

            {rows.length === 0 ? (
              <div className="text-sm text-slate-500">Sin bloques</div>
            ) : (
              <div className="space-y-2">
                {rows.map((r, idx) => (
                  <div
                    key={idx}
                    className="grid grid-cols-12 gap-2 items-center"
                  >
                    <label className="col-span-2 text-xs text-slate-600">
                      Inicio
                    </label>
                    <input
                      type="time"
                      value={r.start}
                      onChange={(e) =>
                        setRows((prev) =>
                          (prev || []).map((x, i) =>
                            i === idx ? { ...x, start: e.target.value } : x
                          )
                        )
                      }
                      className="col-span-4 border rounded-md px-2 py-1 text-sm"
                    />
                    <label className="col-span-2 text-xs text-slate-600">
                      Fin
                    </label>
                    <input
                      type="time"
                      value={r.end}
                      onChange={(e) =>
                        setRows((prev) =>
                          (prev || []).map((x, i) =>
                            i === idx ? { ...x, end: e.target.value } : x
                          )
                        )
                      }
                      className="col-span-3 border rounded-md px-2 py-1 text-sm"
                    />
                    <button
                      onClick={() =>
                        setRows((prev) =>
                          (prev || []).filter((_, i) => i !== idx)
                        )
                      }
                      className="col-span-1 text-red-600 hover:underline text-sm"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  const HorarioBaseEditor = () => (
    <div className="space-y-4">
      <p className="text-sm text-slate-600">
        Configura los días habilitados y el horario de atención de cada día.
      </p>

      <div className="space-y-3">
        {weekDays.map((d) => {
          const config = weeklySchedule?.[d.k] || {};

          return (
            <div
              key={d.k}
              className="border rounded-xl p-4 bg-white"
            >
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h4 className="font-medium">
                      {d.label}
                    </h4>

                    <p className="text-xs text-slate-500">
                      {config?.enabled ? 'Habilitado' : 'Deshabilitado'}
                    </p>
                  </div>

                <button
                  type="button"
                  onClick={() =>
                    setWeeklySchedule((prev) => ({
                      ...prev,
                      [d.k]: {
                        ...prev[d.k],
                        enabled: !prev[d.k]?.enabled,
                      },
                    }))
                  }
                  className={`
                    relative inline-flex h-6 w-11 items-center rounded-full transition-colors
                    ${config?.enabled ? 'bg-indigo-600' : 'bg-slate-300'}
                  `}
                >
                  <span
                    className={`
                      inline-block h-4 w-4 transform rounded-full bg-white transition-transform
                      ${config?.enabled ? 'translate-x-6' : 'translate-x-1'}
                    `}
                  />
                </button>
              </div>

              {config?.enabled && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-slate-600 mb-1">
                      Inicio
                    </label>

                    <input
                      type="time"
                      value={config?.start || '07:30'}
                      onChange={(e) =>
                        setWeeklySchedule((prev) => ({
                          ...prev,
                          [d.k]: {
                            ...prev[d.k],
                            start: e.target.value,
                          },
                        }))
                      }
                      className="w-full border rounded-md px-3 py-2 text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-xs text-slate-600 mb-1">
                      Fin
                    </label>

                    <input
                      type="time"
                      value={config?.end || '20:00'}
                      onChange={(e) =>
                        setWeeklySchedule((prev) => ({
                          ...prev,
                          [d.k]: {
                            ...prev[d.k],
                            end: e.target.value,
                          },
                        }))
                      }
                      className="w-full border rounded-md px-3 py-2 text-sm"
                    />
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {loadingBase && (
        <p className="text-xs text-slate-400">
          Cargando configuración…
        </p>
      )}
    </div>
  );

  // ---------- Render ----------
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div
        className="
          w-[94%]
          max-w-3xl
          rounded-2xl 
          bg-white 
          shadow-2xl 
          overflow-hidden
          max-h-[90vh]
          flex
          flex-col
        "
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">
              Editar breaks
            </h3>
            <p className="text-xs text-slate-500">
              Configurando agenda para el profesional seleccionado.
            </p>
          </div>
          <button
            onClick={onClose}
            className="h-9 w-9 rounded-full hover:bg-slate-100 flex items-center justify-center"
          >
            ✕
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b px-6 pt-4">
          <button
            onClick={() => setTab('weekly')}
            className={`px-3 py-2 text-sm rounded-t-lg font-medium ${
              tab === 'weekly'
                ? 'bg-slate-900 text-white'
                : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            Plantilla semanal
          </button>
          <button
            onClick={() => setTab('daily')}
            className={`px-3 py-2 text-sm rounded-t-lg font-medium ${
              tab === 'daily'
                ? 'bg-slate-900 text-white'
                : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            Día específico
          </button>
          <button
            onClick={() => setTab('base')}
            className={`px-3 py-2 text-sm rounded-t-lg font-medium ${
              tab === 'base'
                ? 'bg-slate-900 text-white'
                : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            Horario semanal
          </button>
        </div>

        {/* Body */}
        <div className="p-6 flex-1 overflow-y-auto">
          {tab === 'daily' ? (
            <div className="space-y-5">
              <div className="flex items-center gap-2">
                <input
                  type="date"
                  value={newDate}
                  onChange={(e) => setNewDate(e.target.value)}
                  className="border rounded-md px-3 py-2 text-sm"
                />
                <button
                  onClick={addDay}
                  className="px-3 py-2 text-sm border rounded-md hover:bg-slate-50"
                >
                  + Agregar día
                </button>
              </div>

              {selectedDays.map((d) => (
                <DayCard key={d} date={d} />
              ))}
            </div>
          ) : tab === 'weekly' ? (
            <WeeklyEditor />
          ) : (
            <HorarioBaseEditor />
          )}
        </div>

        {/* Nota */}
        <div className="px-6 pt-1 pb-2 text-[11px] text-slate-500">
          * Los breaks se aplican al mostrar/abrir los slots de agenda. No se
          modifican reservas ya creadas.
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="px-3 py-2 rounded-md bg-slate-100 hover:bg-slate-200"
          >
            Cerrar
          </button>

          {tab === 'daily' && (
            <button
              onClick={saveDaily}
              disabled={saving}
              className="px-3 py-2 rounded-md bg-indigo-600 text-white hover:bg-indigo-700"
            >
              {saving ? 'Guardando…' : 'Guardar días específicos'}
            </button>
          )}

          {tab === 'weekly' && (
            <button
              onClick={saveWeekly}
              className="px-3 py-2 rounded-md bg-indigo-600 text-white hover:bg-indigo-700"
            >
              Guardar plantilla semanal
            </button>
          )}

          {tab === 'base' && (
            <button
              onClick={saveHorarioBase}
              className="px-3 py-2 rounded-md bg-indigo-600 text-white hover:bg-indigo-700"
            >
              Guardar horario base
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
